import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { PipelineEntradaGeral, PipelineEtapa, PipelineResumo } from '@/types/gestaoLicitacoes';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface GestaoLicitacoesGeralFiltros {
  empresaIds?: number[];
  uf?: string;
  categoria?: string;
  modalidade?: string;
}

function buildGeralParams(filtros?: GestaoLicitacoesGeralFiltros) {
  const params: Record<string, string> = {};
  if (filtros?.empresaIds && filtros.empresaIds.length > 0) params.empresa_ids = filtros.empresaIds.join(',');
  if (filtros?.uf) params.uf = filtros.uf;
  if (filtros?.categoria) params.categoria = filtros.categoria;
  if (filtros?.modalidade) params.modalidade = filtros.modalidade;
  return params;
}

export function pipelineGeralQueryKey(filtros?: GestaoLicitacoesGeralFiltros) {
  return ['gestao-licitacoes-pipeline-geral', filtros];
}

export function resumoGeralQueryKey(filtros?: GestaoLicitacoesGeralFiltros) {
  return ['gestao-licitacoes-resumo-geral', filtros];
}

export function useGestaoLicitacoesPipelineGeral(filtros?: GestaoLicitacoesGeralFiltros) {
  return useQuery<PipelineEntradaGeral[]>({
    queryKey: pipelineGeralQueryKey(filtros),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PipelineEntradaGeral[]>>('/empresas/pipeline/geral', {
        params: buildGeralParams(filtros),
      });
      return response.data;
    },
  });
}

export function useGestaoLicitacoesResumoGeral(filtros?: GestaoLicitacoesGeralFiltros) {
  return useQuery<PipelineResumo>({
    queryKey: resumoGeralQueryKey(filtros),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PipelineResumo>>('/empresas/pipeline/geral/resumo', {
        params: buildGeralParams(filtros),
      });
      return response.data;
    },
  });
}

export function useMoverEtapaPipelineGeral(filtros?: GestaoLicitacoesGeralFiltros) {
  const queryClient = useQueryClient();
  const queryKey = pipelineGeralQueryKey(filtros);

  return useMutation({
    mutationFn: async ({ empresaId, licitacaoId, etapa }: { empresaId: number; licitacaoId: number; etapa: PipelineEtapa }) => {
      const response = await api.patch<ApiResponse<PipelineEntradaGeral>>(
        `/empresas/${empresaId}/pipeline/${licitacaoId}`,
        { etapa }
      );
      return response.data;
    },
    onMutate: async ({ empresaId, licitacaoId, etapa }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PipelineEntradaGeral[]>(queryKey);

      queryClient.setQueryData<PipelineEntradaGeral[]>(queryKey, (old) =>
        (old || []).map((entrada) =>
          entrada.licitacaoId === licitacaoId && entrada.empresaId === empresaId ? { ...entrada, etapa } : entrada
        )
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['gestao-licitacoes-pipeline-geral'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-licitacoes-resumo-geral'] });
    },
  });
}

export function useAdicionarAoPipeline(empresaId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (licitacaoId: number) => {
      const response = await api.post<ApiResponse<PipelineEntradaGeral>>(`/empresas/${empresaId}/pipeline`, {
        licitacaoId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestao-licitacoes-pipeline-geral'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-licitacoes-resumo-geral'] });
    },
  });
}
